/**
 * Simplified UTM tracking utility
 * Captures UTM parameters and stores them for API calls
 */

// List of UTM parameters to track
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id'
];

// Storage key for UTM parameters
const UTM_STORAGE_KEY = 'fleek_utm_params';

/**
 * Captures UTM parameters from URL and stores them in localStorage
 * Always overwrites existing UTM data with latest source
 */
const captureUTMParams = () => {
  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};
  let hasUtmParams = false;

  // Extract UTM parameters from URL
  UTM_PARAMS.forEach(param => {
    if (urlParams.has(param)) {
      utmParams[param] = urlParams.get(param);
      hasUtmParams = true;
    }
  });

  // If UTM parameters exist in the URL, overwrite existing data
  if (hasUtmParams) {
    // Add timestamp and page info for attribution
    utmParams.timestamp = Date.now();
    utmParams.landing_page = window.location.pathname;
    utmParams.referrer = document.referrer;
    
    // Always overwrite - latest UTM source wins
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    console.log('UTM parameters captured (latest source):', utmParams);
  }
};

/**
 * Retrieves stored UTM parameters
 * @returns {Object|null} UTM parameters object or null if not available
 */
const getUTMParams = () => {
  const storedParams = localStorage.getItem(UTM_STORAGE_KEY);
  if (!storedParams) return null;
  
  try {
    return JSON.parse(storedParams);
  } catch (e) {
    console.error('Error parsing stored UTM parameters', e);
    return null;
  }
};

/**
 * Adds UTM parameters to API payload as 'metadata' field for order creation
 * @param {Object} payload - The original API request payload
 * @returns {Object} Updated payload with UTM parameters in metadata field
 */
const addUTMNotesToPayload = (payload = {}) => {
  const utmParams = getUTMParams();
  if (!utmParams) return payload;

  // Create UTM metadata object for API
  const utmMetadata = {
    attribution: {
      source: utmParams.utm_source || null,
      medium: utmParams.utm_medium || null,
      campaign: utmParams.utm_campaign || null,
      term: utmParams.utm_term || null,
      content: utmParams.utm_content || null,
      id: utmParams.utm_id || null,
      landing_page: utmParams.landing_page || null,
      referrer: utmParams.referrer || null,
      captured_at: utmParams.timestamp || null
    }
  };

  return {
    ...payload,
    metadata: utmMetadata
  };
};

/**
 * Gets UTM data for analytics (Facebook CAPI, etc.)
 * @returns {Object} UTM parameters for analytics platforms
 */
const getUTMForAnalytics = () => {
  const utmParams = getUTMParams();
  if (!utmParams) return {};

  // Return clean UTM object for analytics
  return {
    utm_source: utmParams.utm_source,
    utm_medium: utmParams.utm_medium,
    utm_campaign: utmParams.utm_campaign,
    utm_term: utmParams.utm_term,
    utm_content: utmParams.utm_content,
    utm_id: utmParams.utm_id
  };
};

/**
 * Clears stored UTM parameters
 */
const clearUTMParams = () => {
  localStorage.removeItem(UTM_STORAGE_KEY);
  console.log('UTM parameters cleared');
};

const UTMTracker = {
  captureUTMParams,
  getUTMParams,
  addUTMNotesToPayload, // Keep same name for backward compatibility
  getUTMForAnalytics,
  clearUTMParams
};

export default UTMTracker; 